import React from "react";
import { motion } from "framer-motion";
import { cn } from "../utils/cn";
import { CheckIcon } from "@heroicons/react/24/outline";

export interface SelectableOption {
  value: string;
  label: string;
  description?: string;
}

export interface SelectableGroupProps {
  label?: string;
  options: SelectableOption[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  multiple?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  disabled?: boolean;
  excludeValues?: string[];
  columns?: 1 | 2 | 3 | 4;
}

export const SelectableGroup: React.FC<SelectableGroupProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  multiple = true,
  error,
  helperText,
  className,
  disabled = false,
  excludeValues = [],
  columns = 2,
}) => {
  const handleToggle = (value: string) => {
    if (disabled) return;

    if (multiple) {
      const isSelected = selectedValues.includes(value);
      if (isSelected) {
        onChange(selectedValues.filter((v) => v !== value));
      } else {
        onChange([...selectedValues, value]);
      }
    } else {
      onChange([value]);
    }
  };

  const filteredOptions = options.filter(
    (option) => !excludeValues.includes(option.value)
  );

  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
  };

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <label className="block text-sm font-bold text-tiber-900 mb-2">
          {label}
        </label>
      )}
      <div className={cn("grid gap-3", gridCols[columns])}>
        {filteredOptions.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          return (
            <motion.button
              key={option.value}
              type="button"
              onClick={() => handleToggle(option.value)}
              disabled={disabled}
              className={cn(
                "relative p-4 rounded-lg border-solid border-2 text-left transition-all duration-200",
                "focus:outline-none focus:ring-0",
                isSelected
                  ? "border-solid border-2 border-tiber-900 bg-tiber-900/5 text-tiber-900 focus:ring-tiber-900"
                  : "border-solid border-2 border-tiber-200 bg-white text-tiber-900 hover:border-tiber-300 hover:bg-romance-50 focus:ring-tiber-900",
                error && "border-red-500",
                disabled && "opacity-50 cursor-not-allowed"
              )}
              whileHover={disabled ? {} : { scale: 1.02 }}
              whileTap={disabled ? {} : { scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-medium text-sm">{option.label}</div>
                  {option.description && (
                    <div className="text-xs text-titan mt-1">
                      {option.description}
                    </div>
                  )}
                </div>
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="shrink-0"
                  >
                    <CheckIcon className="w-5 h-5 text-tiber-900" />
                  </motion.div>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {helperText && !error && (
        <p className="mt-1 text-sm text-tiber-800">{helperText}</p>
      )}
    </div>
  );
};

SelectableGroup.displayName = "SelectableGroup";
